/**
 * Mandat-Tab in der Klienten-Detailansicht (Kanzlei-Portal, Sep 2026).
 * Bündelt alles, was vorher das Dashboard überladen hat: Stammdaten & Einstellungen
 * (Steuernummer, Soll/Ist, UVA-Rhythmus), Grenzen-Monitoring, UVA (Status, Berechnung,
 * Historie), Jahreserklärungen und die Honorarnoten NUR dieses Mandanten.
 */
import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import LexUvaDrawer from '@/components/lex/LexUvaDrawer';
import LexYearEndDrawer from '@/components/lex/LexYearEndDrawer';
import LexHonorarnotenPanel from '@/components/lex/LexHonorarnotenPanel';
import LexEstVzPanel from '@/components/lex/LexEstVzPanel';
import LexUvaYearPanel from '@/components/lex/LexUvaYearPanel';
import LexVstpPanel from '@/components/lex/LexVstpPanel';
import { Scale, FileSpreadsheet, ReceiptText, Landmark, Check } from 'lucide-react';
import '@/styles/lex.css';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmtEur = (v) =>
  v == null ? '—' : new Intl.NumberFormat('de-AT', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(v);
const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString('de-AT', { day: '2-digit', month: '2-digit', year: 'numeric' }); }
  catch { return '—'; }
};

const ThresholdBar = ({ t, testId }) => {
  const color = t.level === 'alert' ? '#DC2626' : t.level === 'warn' ? '#D97706' : '#0A1128';
  return (
    <div data-testid={testId}>
      <div className="flex justify-between text-xs mb-1">
        <span className="text-slate-500">{t.label}</span>
        <span className="lex-mono font-semibold" style={{ color }}>{fmtEur(t.value)} / {fmtEur(t.limit)}</span>
      </div>
      <div className="h-1.5 bg-slate-100 w-full">
        <div className="h-1.5 transition-all" style={{ width: `${Math.min(t.pct, 100)}%`, background: color }} />
      </div>
    </div>
  );
};

// Segment-Umschalter (2 Optionen) im lex-Design
const Segment = ({ value, options, onChange, saving, testIdPrefix }) => (
  <div className="inline-flex border border-slate-200">
    {options.map(([val, label]) => (
      <button key={val} type="button" disabled={saving}
        onClick={() => val !== value && onChange(val)}
        className="px-3 py-1.5 text-xs uppercase tracking-wider font-semibold transition-colors disabled:opacity-60"
        style={val === value ? { background: '#0A1128', color: '#C5A059' } : { background: '#fff', color: '#64748B' }}
        data-testid={`${testIdPrefix}-${val}`}>
        {val === value && <Check className="h-3 w-3 inline mr-1 -mt-0.5" />}{label}
      </button>
    ))}
  </div>
);

export default function LexMandatTab({ clientId }) {
  const [client, setClient] = useState(null);
  const [noAccess, setNoAccess] = useState(false);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stnrInput, setStnrInput] = useState('');
  const [saving, setSaving] = useState(false);
  const [showUva, setShowUva] = useState(false);
  const [uvaPeriod, setUvaPeriod] = useState(null);
  const [yearKey, setYearKey] = useState(0);
  const [showYearEnd, setShowYearEnd] = useState(false);

  const load = useCallback(() => {
    axios.get(`${API}/lex/advisor/cockpit`)
      .then((res) => {
        if (res.data.data_access === false) { setNoAccess(true); return; }
        const found = (res.data.clients || []).find((c) => c.client_id === clientId) || null;
        setClient(found);
        if (found) setStnrInput(found.tax_number || '');
      })
      .catch(() => {})
      .finally(() => setLoading(false));
    axios.get(`${API}/lex/advisor/uva-history`)
      .then((res) => setHistory((res.data.history || {})[clientId] || []))
      .catch(() => {});
  }, [clientId]);

  useEffect(() => { load(); }, [load]);

  const post = async (path, body, okMsg) => {
    setSaving(true);
    try {
      await axios.post(`${API}/lex/advisor/clients/${clientId}/${path}`, body);
      toast.success(okMsg);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Änderung fehlgeschlagen.');
    } finally { setSaving(false); }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-slate-200 border-t-[#C5A059]" />
      </div>
    );
  }
  if (noAccess) {
    return (
      <div className="bg-white border border-slate-200 p-8 text-center text-sm text-slate-500" data-testid="lex-mandat-no-access">
        Kein Mandantendaten-Zugriff — ein Team-Kollege muss Ihren Zugriff unter Sicherheit → Team & Datenzugriff freischalten.
      </div>
    );
  }
  if (!client) {
    return (
      <div className="bg-white border border-slate-200 p-8 text-center text-sm text-slate-500" data-testid="lex-mandat-not-found">
        Kein lex-Mandat für diesen Klienten gefunden.
      </div>
    );
  }

  const uva = client.ustva || {};
  const uvaColor = uva.ustva_status === 'overdue' ? 'text-red-600' : uva.ustva_status === 'ready' ? 'text-emerald-600' : 'text-amber-600';

  return (
    <div className="lex-theme space-y-6" data-testid="lex-mandat-tab">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Stammdaten & Einstellungen */}
        <div className="lex-card p-6" data-testid="lex-mandat-settings-card">
          <div className="flex items-center gap-2 mb-5">
            <Landmark className="h-4 w-4" style={{ color: '#C5A059' }} />
            <h2 className="text-xs uppercase tracking-widest font-semibold text-slate-500">Stammdaten & Einstellungen</h2>
          </div>

          <div className="space-y-5">
            <div>
              <p className="text-xs uppercase tracking-wider font-semibold text-slate-400 mb-1.5">Steuernummer</p>
              <div className="flex gap-2">
                <Input value={stnrInput} onChange={(e) => setStnrInput(e.target.value)}
                  placeholder="z. B. 12-345/6789" className="rounded-none max-w-[220px] lex-mono text-sm"
                  data-testid="lex-mandat-stnr-input" />
                <Button size="sm" variant="outline" className="rounded-none text-xs uppercase tracking-wider"
                  disabled={saving || (stnrInput || '') === (client.tax_number || '')}
                  onClick={() => post('steuernummer', { steuernummer: stnrInput }, 'Steuernummer gespeichert.')}
                  data-testid="lex-mandat-stnr-save">
                  Speichern
                </Button>
              </div>
              {!client.tax_number && <p className="text-xs text-amber-600 mt-1">Fehlt noch — für UVA-QR-Code & FinanzOnline erforderlich.</p>}
            </div>

            <div>
              <p className="text-xs uppercase tracking-wider font-semibold text-slate-400 mb-1.5">Besteuerungsart</p>
              <Segment value={client.tax_scheme} saving={saving}
                options={[['cash_basis', 'Ist'], ['accrual', 'Soll']]}
                onChange={(v) => post('tax-scheme', { scheme: v }, `Besteuerung auf ${v === 'cash_basis' ? 'Ist' : 'Soll'} umgestellt.`)}
                testIdPrefix="lex-mandat-scheme" />
              <p className="text-xs text-slate-400 mt-1">Wirkt sofort auf UVA- und U1-Berechnung.</p>
            </div>

            <div>
              <p className="text-xs uppercase tracking-wider font-semibold text-slate-400 mb-1.5">UVA-Rhythmus</p>
              <Segment value={client.vat_filing_frequency} saving={saving}
                options={[['quarterly', 'Quartalsweise'], ['monthly', 'Monatlich']]}
                onChange={(v) => post('vat-frequency', { frequency: v }, `UVA-Rhythmus auf ${v === 'monthly' ? 'monatlich' : 'quartalsweise'} umgestellt.`)}
                testIdPrefix="lex-mandat-freq" />
              <p className="text-xs text-slate-400 mt-1">§ 21 UStG: ab 100.000 € Vorjahresumsatz ist monatlich verpflichtend.</p>
            </div>

            <LexVstpPanel clientId={clientId} />

            <div className="pt-4 border-t border-slate-100">
              <p className="text-xs uppercase tracking-wider font-semibold text-slate-400 mb-1">Honorar-Modell</p>
              <p className="text-sm text-slate-700 lex-mono" data-testid="lex-mandat-honorar">
                {client.honorar?.mode?.toUpperCase() || '—'}
                {client.honorar?.total ? ` · ${fmtEur(client.honorar.total)}/Monat` : ''}
                {client.honorar?.next_run_date ? ` · nächste Note ${fmtDate(client.honorar.next_run_date)}` : ''}
              </p>
            </div>
          </div>
        </div>

        <LexEstVzPanel clientId={clientId} />

        {/* Grenzen-Monitoring */}
        <div className="lex-card p-6" data-testid="lex-mandat-thresholds-card">
          <div className="flex items-center gap-2 mb-5">
            <Scale className="h-4 w-4" style={{ color: '#C5A059' }} />
            <h2 className="text-xs uppercase tracking-widest font-semibold text-slate-500">Grenzen-Monitoring</h2>
          </div>
          <div className="space-y-4">
            <ThresholdBar t={client.thresholds.kleinunternehmer} testId={`lex-th-ku-${clientId}`} />
            <ThresholdBar t={client.thresholds.uva_rhythmus} testId={`lex-th-uva-${clientId}`} />
            <ThresholdBar t={client.thresholds.pauschalierung} testId={`lex-th-pausch-${clientId}`} />
          </div>
          <div className="mt-5 pt-4 border-t border-slate-100 text-sm text-slate-500 lex-mono">
            Umsatz lfd. Jahr {fmtEur(client.revenue_current_year)} · Vorjahr {fmtEur(client.revenue_prev_year)}
          </div>
        </div>
      </div>

      {/* UVA */}
      <div className="lex-card p-6" data-testid="lex-mandat-uva-card">
        <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
          <div className="flex items-center gap-2">
            <ReceiptText className="h-4 w-4" style={{ color: '#C5A059' }} />
            <h2 className="text-xs uppercase tracking-widest font-semibold text-slate-500">Umsatzsteuer-Voranmeldung</h2>
          </div>
          <div className="flex gap-2">
            <Button size="sm" className="rounded-none text-xs uppercase tracking-wider"
              style={{ background: '#0A1128', color: '#C5A059' }}
              onClick={() => setShowUva(true)} data-testid="lex-mandat-uva-btn">
              UVA berechnen
            </Button>
            <Button variant="outline" size="sm" className="rounded-none text-xs uppercase tracking-wider"
              onClick={() => setShowYearEnd(true)} data-testid="lex-mandat-yearend-btn">
              <FileSpreadsheet className="h-3.5 w-3.5 mr-1" /> Jahreserklärungen
            </Button>
          </div>
        </div>
        <div className="text-sm mb-3">
          <span className="text-slate-500">UVA {uva.next_deadline_period || '—'}: </span>
          <span className={`font-semibold ${uvaColor}`} data-testid="lex-mandat-uva-status">{uva.ustva_status_label || 'Keine UVA'}</span>
          {uva.next_deadline && <span className="text-slate-400 lex-mono text-xs ml-2">fällig {fmtDate(uva.next_deadline)}</span>}
        </div>
        {history.length > 0 && (
          <div className="flex flex-wrap gap-1.5" data-testid="lex-mandat-uva-history">
            {history.slice(0, 12).map((r) => (
              <span key={r.period} title={r.status === 'external'
                  ? `Extern erledigt${r.approved_by_name ? ` · markiert von ${r.approved_by_name}` : ''}${r.external_note ? ` · „${r.external_note}"` : ''}`
                  : `${r.status === 'paid'
                    ? `Bezahlt am ${fmtDate(r.paid_at)} · € ${Number(r.zahllast).toLocaleString('de-AT', { minimumFractionDigits: 2 })}`
                    : `Zahlung offen · fällig ${fmtDate(r.due_date)} · € ${Number(r.zahllast).toLocaleString('de-AT', { minimumFractionDigits: 2 })}`}${r.fo_submitted_at
                    ? ` · FinanzOnline übermittelt am ${fmtDate(r.fo_submitted_at)}`
                    : ' · FinanzOnline-Übermittlung noch nicht vermerkt'}`}
                className="lex-mono text-[11px] px-2 py-0.5 border"
                style={r.status === 'external'
                  ? { borderColor: '#CBD5E1', color: '#64748B', background: '#F1F5F9' }
                  : r.status === 'paid'
                    ? { borderColor: '#16A34A', color: '#166534', background: '#F0FDF4' }
                    : r.zahllast <= 0
                      ? { borderColor: '#CBD5E1', color: '#475569', background: '#F8FAFC' }
                      : { borderColor: '#C5A059', color: '#92650B', background: '#FFFBEB' }}
                data-testid={`lex-mandat-uva-chip-${r.period}`}>
                {r.status === 'external'
                  ? `${r.period_label} extern`
                  : `${r.period_label} ${r.status === 'paid' ? '✓' : r.zahllast <= 0 ? '—' : '⏳'}${r.fo_submitted_at ? ' · FO✓' : ''}`}
              </span>
            ))}
          </div>
        )}
        {history.length === 0 && <p className="text-sm text-slate-400" data-testid="lex-mandat-uva-history-empty">Noch keine freigegebenen UVA-Perioden.</p>}
      </div>

      <LexUvaYearPanel clientId={clientId} refreshKey={yearKey}
        onOpenPeriod={(p) => { setUvaPeriod(p); setShowUva(true); }} />

      {/* Honorarnoten nur dieses Mandanten */}
      <LexHonorarnotenPanel clientId={clientId} />

      {showUva && (
        <LexUvaDrawer client={client} initialPeriod={uvaPeriod}
          onClose={() => { setShowUva(false); setUvaPeriod(null); setYearKey((k) => k + 1); load(); }}
          onDone={() => { setShowUva(false); setUvaPeriod(null); setYearKey((k) => k + 1); load(); }} />
      )}
      {showYearEnd && (
        <LexYearEndDrawer client={client} onClose={() => setShowYearEnd(false)} />
      )}
    </div>
  );
}
